import type { IntlShape } from "react-intl";
import type { CompanyProfileInput } from "./use-company-admin";

// The editable company profile as the form holds it, plus the checks genesis will apply anyway.
//
// THE FIELD SET IS CLOSED. company-profiles/0006-company-page-mvp fixes the MVP catalogue: identity
// (name), industry, size, and ONE free-text "About us" carrying vision, mission, product, culture,
// finances and expectations together. §3 and §6 of that ADR exclude a tagline, a product field,
// separate culture/finance fields and Powers by name. Adding one here needs the ADR amended first.
//
// The logo is in §1 but is not on this form: genesis takes it as a base64 upload with its own size
// and content rules, and an update that omits the field leaves the stored logo untouched. Left out
// deliberately rather than half-built. Contact persons (§4, up to ten flagged members since concept v13) have no
// genesis field at all, so it cannot be built here yet.

/** Free-text mirror of the input, so an emptied number field stays distinguishable from a zero. */
export interface CompanyProfileDraft {
  name: string;
  industryId: string;
  employeeCount: string;
  summary: string;
}

export const EMPTY_COMPANY_DRAFT: CompanyProfileDraft = {
  name: "",
  industryId: "",
  employeeCount: "",
  summary: "",
};

/** genesis's own caps (CompanyController). Mirrored so the admin is told before the round trip. */
export const MAX_NAME_LENGTH = 255;
export const MAX_SUMMARY_LENGTH = 20000;

export interface CompanyProfileSource {
  name: string;
  employeeCount?: number | null;
  summary?: string | null;
  industry?: { id: string } | null;
}

export function draftFromCompany(company: CompanyProfileSource): CompanyProfileDraft {
  return {
    name: company.name,
    industryId: company.industry?.id ?? "",
    employeeCount: company.employeeCount == null ? "" : String(company.employeeCount),
    summary: company.summary ?? "",
  };
}

/**
 * The first localized answer to a bad draft. genesis validates the same four rules and is the one
 * that decides; this only spares the admin a round trip to be told in English with a raw field name.
 */
export function companyDraftError(intl: IntlShape, draft: CompanyProfileDraft): string | null {
  const name = draft.name.trim();
  if (!name) {
    return intl.formatMessage({
      id: "company.admin.error.nameRequired",
      defaultMessage: "A company name is required.",
    });
  }
  if (name.length > MAX_NAME_LENGTH) {
    return intl.formatMessage(
      {
        id: "company.admin.error.nameTooLong",
        defaultMessage: "The company name may be at most {max, number} characters.",
      },
      { max: MAX_NAME_LENGTH },
    );
  }
  if (draft.summary.length > MAX_SUMMARY_LENGTH) {
    return intl.formatMessage(
      {
        id: "company.admin.error.summaryTooLong",
        defaultMessage: "The About us text may be at most {max, number} characters.",
      },
      { max: MAX_SUMMARY_LENGTH },
    );
  }
  const employeeCount = draft.employeeCount.trim();
  // Digits only: genesis refuses anything below 0, and a decimal or an exponent would silently
  // round on the way into an Int column.
  if (employeeCount !== "" && !/^\d+$/.test(employeeCount)) {
    return intl.formatMessage({
      id: "company.admin.error.employeeCount",
      defaultMessage: "The number of employees must be a whole number of 0 or more.",
    });
  }
  return null;
}

/**
 * The wire shape. Every optional field becomes an explicit null when empty: genesis reads an update
 * off the properties the request actually carried, so a field left out keeps its stored value and
 * clearing one has to travel as a present null.
 */
export function toCompanyProfileInput(draft: CompanyProfileDraft): CompanyProfileInput {
  const employeeCount = draft.employeeCount.trim();
  const summary = draft.summary.trim();
  return {
    name: draft.name.trim(),
    industryId: draft.industryId || null,
    employeeCount: employeeCount === "" ? null : Number(employeeCount),
    summary: summary === "" ? null : summary,
  };
}
